"use client"

import { useCallback, useMemo } from "react"
import type { Editor } from "@tiptap/react"
import { deleteRow, deleteColumn, CellSelection, deleteTable } from "@tiptap/pm/tables"
import type { Transaction } from "@tiptap/pm/state"

// --- Hooks ---
import { useTiptapEditor } from "@editor/hooks/use-tiptap-editor"

// --- Lib ---
import { isExtensionAvailable } from "@editor/lib/tiptap-utils"
import type { Orientation } from "@editor/components/tiptap-node/table-node/lib/tiptap-table-utils"
import {
  getTable,
} from "@editor/components/tiptap-node/table-node/lib/tiptap-table-utils"

// --- Icons ---
import { TrashIcon } from "@editor/components/tiptap-icons/trash-icon"

export interface UseTableDeleteConfig {
  /**
   * The Tiptap editor instance. If omitted, the hook will use
   * the context/editor from `useTiptapEditor`.
   */
  editor?: Editor | null
  /**
   * The position of the table in the document.
   */
  tablePos?: number
  /**
   * Hide the button when deletion isn't currently possible.
   * @default false
   */
  hideWhenUnavailable?: boolean
  /**
   * Callback function called after a successful delete.
   */
  onDeleted?: () => void
}

const REQUIRED_EXTENSIONS = ["table"]


/**
 * Checks if a table delete can be performed
 * in the current editor state.
 */
function canDelete({
  editor,
  tablePos,
}: {
  editor: Editor | null
  tablePos?: number
}): boolean {
  if (
    !editor ||
    !editor.isEditable ||
    !isExtensionAvailable(editor, REQUIRED_EXTENSIONS)
  ) {
    return false
  }

  try {
    const table = getTable(editor, tablePos)
    if (!table) return false

    return true
  } catch {
    return false
  }
}

/**
 * Determines if the delete button should be shown
 * based on editor state and config.
 */
function shouldShowButton({
  editor,
  hideWhenUnavailable,
  tablePos,
}: {
  editor: Editor | null
  index?: number
  orientation?: Orientation
  hideWhenUnavailable: boolean
  tablePos?: number
}): boolean {
  if (!editor || !editor.isEditable) return false
  if (!isExtensionAvailable(editor, REQUIRED_EXTENSIONS)) return false
  return hideWhenUnavailable
    ? canDelete({ editor, tablePos })
    : true
}

/**
 * Custom hook that provides **table deletion**
 * functionality for the Tiptap editor.
 */
export function useTableDelete(config: UseTableDeleteConfig) {
  const {
    editor: providedEditor,
    tablePos,
    hideWhenUnavailable = false,
    onDeleted,
  } = config

  const { editor } = useTiptapEditor(providedEditor)

  const isVisible = shouldShowButton({
    editor,
    hideWhenUnavailable,
  })

  const canPerformDelete = canDelete({
    editor,
    tablePos,
  })

  const handleDelete = useCallback(() => {
    if (!editor) return false;
    try {
      const success = editor.commands.deleteTable();
      if (success) onDeleted?.();
      return success;
    } catch (error) {
      console.error("Error deleting table:", error);
      return false;
    }
  }, [editor, tablePos, onDeleted])

  const label = useMemo(() => {
    return "Delete table"
  }, [])

  return {
    isVisible,
    canDelete: canPerformDelete,
    handleDelete,
    label,
    Icon: TrashIcon,
  }
}
